/**
 * Material Request Note Form
 * Create / edit a Material Request Note (draft). Inventory items only.
 * Header: cost center (required) + optional request warehouse + priority.
 * Line items: inventory item + requested quantity.
 */

import React, { useState, useEffect } from 'react';
import SearchableSelect from '../../Common/SearchableSelect';
import SearchableCostCenterDropdown from '../../Common/SearchableCostCenterDropdown';
import inventoryApi from '../../../services/inventoryApi';
import adminApi from '../../../services/adminApi';
import { useLocation } from '../../../contexts/LocationContext';
import toast from 'react-hot-toast';

const WAREHOUSE_TYPES = ['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility', 'transit_hub', 'quarantine_facility', 'returns_center'];

const emptyLine = () => ({ item: '', itemCode: '', itemDescription: '', uom: '', quantityRequested: '' });

const MaterialRequestNoteForm = ({ mrn, onClose, onSuccess }) => {
  const isEdit = !!mrn;
  const { currentLocation } = useLocation();
  const currentLocationId = currentLocation?._id || currentLocation?.id;

  const [formData, setFormData] = useState({
    costCenter: mrn?.costCenter?._id || mrn?.costCenter || '',
    costCenterName: mrn?.costCenterName || '',
    requestWarehouse: mrn?.requestWarehouse || '',
    requestWarehouseName: mrn?.requestWarehouseName || '',
    priority: mrn?.priority || 'normal',
    requestDate: mrn?.requestDate ? new Date(mrn.requestDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
    notes: mrn?.notes || '',
    lineItems: (mrn?.lineItems?.length
      ? mrn.lineItems.map(li => ({
          item: li.item?._id || li.item || '',
          itemCode: li.itemCode || '',
          itemDescription: li.itemDescription || '',
          uom: li.uom || '',
          quantityRequested: li.quantityRequested ?? ''
        }))
      : [emptyLine()])
  });

  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      try {
        const [locRes, itemRes] = await Promise.all([
          // Scope warehouses to the logged-in location only.
          currentLocationId
            ? adminApi.getLocations({ type: 'warehouse', parentId: currentLocationId, limit: 1000 }).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
          inventoryApi.getItems({ limit: 1000 }).catch(() => ({ data: [] }))
        ]);

        const locs = locRes.data?.data || locRes.data || [];
        setWarehouses((Array.isArray(locs) ? locs : []).filter(l => WAREHOUSE_TYPES.includes(l.type)));

        // Inventory items only — exclude fixed-asset-type / non-trackable items.
        const allItems = itemRes.data || [];
        setItems((Array.isArray(allItems) ? allItems : []).filter(
          it => it.type !== 'asset' && it.inventory?.trackInventory !== false
        ));
      } catch (err) {
        console.error('Error loading MRN form data:', err);
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, [currentLocationId]);

  const warehouseOptions = warehouses.map(w => ({ value: w._id, label: w.name }));

  const itemOptions = items.map(it => ({ value: it._id, label: `${it.sku} - ${it.name}` }));

  const handleHeaderChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCostCenterChange = (id, label) => {
    setFormData(prev => ({ ...prev, costCenter: id, costCenterName: label || '' }));
    setErrors(prev => ({ ...prev, costCenter: '' }));
  };

  const handleWarehouseChange = (e) => {
    const value = e.target.value;
    const wh = warehouses.find(w => w._id === value);
    setFormData(prev => ({ ...prev, requestWarehouse: value, requestWarehouseName: wh ? wh.name : '' }));
  };

  const handleLineChange = (index, field, value) => {
    setFormData(prev => {
      const lineItems = [...prev.lineItems];
      const line = { ...lineItems[index], [field]: value };

      if (field === 'item') {
        const selected = items.find(it => it._id === value);
        if (selected) {
          line.itemCode = selected.sku;
          line.itemDescription = selected.description || selected.name;
          line.uom = selected.inventory?.unit || '';
        }
      }

      lineItems[index] = line;
      return { ...prev, lineItems };
    });
    setErrors(prev => ({ ...prev, [`line_${index}_${field}`]: '' }));
  };

  const addLineItem = () => {
    setFormData(prev => ({ ...prev, lineItems: [...prev.lineItems, emptyLine()] }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({ ...prev, lineItems: prev.lineItems.filter((_, i) => i !== index) }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.costCenter) newErrors.costCenter = 'Cost center is required';
    if (!currentLocationId) newErrors.form = 'No active location selected — switch to a location before creating a request';

    const validLines = formData.lineItems.filter(li => li.item && parseFloat(li.quantityRequested) > 0);
    if (validLines.length === 0) {
      newErrors.lineItems = 'Add at least one item with a quantity greater than 0';
    }

    formData.lineItems.forEach((li, index) => {
      if (li.item && !(parseFloat(li.quantityRequested) > 0)) {
        newErrors[`line_${index}_quantityRequested`] = 'Enter a quantity';
      }
      if (!li.item && parseFloat(li.quantityRequested) > 0) {
        newErrors[`line_${index}_item`] = 'Select an item';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        costCenter: formData.costCenter,
        costCenterName: formData.costCenterName,
        // Every MRN is scoped to the creating location — the list is filtered by this
        // field against the X-Location-Id header, so it must always be set.
        requestLocation: currentLocationId || undefined,
        requestLocationName: currentLocation?.name || undefined,
        requestWarehouse: formData.requestWarehouse || undefined,
        requestWarehouseName: formData.requestWarehouseName || undefined,
        priority: formData.priority,
        requestDate: formData.requestDate,
        notes: formData.notes,
        lineItems: formData.lineItems
          .filter(li => li.item && parseFloat(li.quantityRequested) > 0)
          .map(li => ({
            item: li.item,
            itemCode: li.itemCode,
            itemDescription: li.itemDescription,
            uom: li.uom,
            quantityRequested: parseFloat(li.quantityRequested)
          }))
      };

      if (isEdit) {
        await inventoryApi.updateMaterialRequestNote(mrn._id, payload);
        toast.success('Material request note updated');
      } else {
        await inventoryApi.createMaterialRequestNote(payload);
        toast.success('Material request note saved as draft');
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save material request note');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (hasError) =>
    `w-full text-xs px-2 py-1.5 border rounded focus:outline-none focus:ring-1 ${
      hasError ? 'border-red-300 bg-red-50 focus:ring-red-400' : 'border-gray-300 focus:ring-indigo-500'
    }`;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">
              {isEdit ? `Edit ${mrn.mrnNo}` : 'New Material Request Note'}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-4 py-3">
            {dataLoading ? (
              <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
            ) : (
              <>
                {/* Header fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Cost Center <span className="text-red-500">*</span>
                    </label>
                    <SearchableCostCenterDropdown
                      value={formData.costCenter}
                      displayLabel={formData.costCenterName}
                      onChange={handleCostCenterChange}
                      placeholder="Select cost center"
                      error={!!errors.costCenter}
                      menuPlacement="down"
                    />
                    {errors.costCenter && <p className="mt-1 text-xs text-red-600">{errors.costCenter}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Request Warehouse</label>
                    <SearchableSelect
                      value={formData.requestWarehouse}
                      onChange={handleWarehouseChange}
                      options={warehouseOptions}
                      placeholder="Select warehouse"
                      size="sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => handleHeaderChange('priority', e.target.value)}
                      className={inputCls(false)}
                    >
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Request Date</label>
                    <input
                      type="date"
                      value={formData.requestDate}
                      onChange={(e) => handleHeaderChange('requestDate', e.target.value)}
                      className={inputCls(false)}
                    />
                  </div>
                </div>

                {/* Line items */}
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Requested Items</h4>
                  <button
                    type="button"
                    onClick={addLineItem}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded transition-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Row
                  </button>
                </div>

                {errors.lineItems && <p className="text-xs text-red-600 mb-1">{errors.lineItems}</p>}

                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="min-w-max w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 w-1/2">Item</th>
                        <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">UoM</th>
                        <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Qty Requested</th>
                        <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {formData.lineItems.map((line, index) => (
                        <tr key={index}>
                          <td className="px-2 py-1.5 align-top">
                            <SearchableSelect
                              value={line.item}
                              onChange={(e) => handleLineChange(index, 'item', e.target.value)}
                              options={itemOptions}
                              placeholder="Select an item"
                              error={errors[`line_${index}_item`] || ''}
                              size="sm"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-center text-xs text-gray-600 align-top">
                            <div className="pt-1.5">{line.uom || '—'}</div>
                          </td>
                          <td className="px-2 py-1.5 align-top">
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={line.quantityRequested}
                              onChange={(e) => handleLineChange(index, 'quantityRequested', e.target.value)}
                              className={`${inputCls(!!errors[`line_${index}_quantityRequested`])} text-center`}
                              placeholder="0"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-center align-top">
                            {formData.lineItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLineItem(index)}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all mt-0.5"
                                title="Remove"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Notes */}
                <div className="mt-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => handleHeaderChange('notes', e.target.value)}
                    className={inputCls(false)}
                    placeholder="Optional notes"
                  />
                </div>

                {errors.form && <p className="mt-2 text-xs text-red-600">{errors.form}</p>}

                {/* Actions */}
                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded transition-all disabled:opacity-50"
                  >
                    {loading ? 'Saving…' : isEdit ? 'Update Draft' : 'Save Draft'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default MaterialRequestNoteForm;
